// components/shared/DataList.js — the one list every staff screen uses (owner's rule:
// every list searchable, sortable, CSV). Search-as-you-type over all columns, click a
// header to sort (a "Sort by" box on phones), optional filters, newest first by default,
// and a CSV button that downloads exactly the rows shown, in the order shown.
//
// columns: [{ key, label, render?(row), value?(row) (what search/sort/CSV use),
//             sortValue?(row) (sort only, when the order differs from the text, e.g. a stage order),
//             type?: 'date'|'number', sortable?: false, csv?: false, search?: false }]
// rowClass?(row): extra classes for a row (e.g. highlight new / dim parked rows).
// pinTop?(row): rows for which this is true stay above the rest whatever the sort (e.g. today's finds).
// csv={false}: hide the CSV button (e.g. where exporting needs a permission the viewer lacks).
// The toolbar and note are hidden when printing (print:hidden), so printed lists show only the rows.
// note: a small line under the toolbar (e.g. "searching the 500 rows loaded" for server-paged lists).
import React, { useMemo, useState } from 'react';
import { applyList, pinRows, distinctValues, defaultSortFor, toCsv, cellValue } from '../../utils/listTools';

const fmt = (v, type) => {
    if (v === null || v === undefined || v === '') return '';
    if (type === 'date') {
        const s = String(v);
        const d = new Date(s.length === 10 ? `${s}T12:00:00` : s);
        return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString();
    }
    return String(v);
};

export function downloadCsv(name, text) {
    const blob = new Blob([`﻿${text}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${name || 'list'}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function DataList({ rows, columns, rowKey, csvName, defaultSort, filters: filterDefs = [], onRowClick, empty = 'Nothing to show.', actions, toolbar = true, rowClass, pinTop, note, csv = true }) {
    const [query, setQuery] = useState('');
    const [sort, setSort] = useState(defaultSort || defaultSortFor(columns));
    const [filters, setFilters] = useState({});
    const shown = useMemo(() => pinRows(applyList(rows || [], columns, { query, filters, sort }), pinTop), [rows, columns, query, filters, sort, pinTop]);
    const sortable = columns.filter((c) => c.sortable !== false);
    const clickSort = (c) => {
        if (c.sortable === false) return;
        setSort((s) => (s && s.key === c.key ? { key: c.key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: c.key, dir: c.type === 'date' ? 'desc' : 'asc' }));
    };
    const arrow = (c) => (sort && sort.key === c.key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '');
    const keyOf = (r, i) => (typeof rowKey === 'function' ? rowKey(r) : r[rowKey]) ?? r._key ?? i;
    return (
        <div>
            {toolbar && (
                <div className="flex flex-wrap gap-2 items-center mb-3 print:hidden">
                    <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search every column…"
                        aria-label="Search" className="flex-1 min-w-[12rem] p-2 border border-slate-300 rounded text-sm" />
                    {filterDefs.map((f) => {
                        const col = columns.find((c) => c.key === f.key) || { key: f.key };
                        const opts = f.options || distinctValues(rows || [], col);
                        return (
                            <select key={f.key} aria-label={f.label} value={filters[f.key] || ''} onChange={(e) => setFilters({ ...filters, [f.key]: e.target.value })}
                                className="p-2 border border-slate-300 rounded text-sm bg-white">
                                <option value="">{f.label}: all</option>
                                {opts.map((o) => <option key={o} value={o}>{o}</option>)}
                            </select>
                        );
                    })}
                    <select aria-label="Sort by" className="md:hidden p-2 border border-slate-300 rounded text-sm bg-white"
                        value={sort ? `${sort.key}:${sort.dir}` : ''} onChange={(e) => { const [key, dir] = e.target.value.split(':'); setSort({ key, dir }); }}>
                        {sortable.flatMap((c) => [<option key={`${c.key}:asc`} value={`${c.key}:asc`}>Sort: {c.label} ▲</option>, <option key={`${c.key}:desc`} value={`${c.key}:desc`}>Sort: {c.label} ▼</option>])}
                    </select>
                    <span className="text-xs text-slate-500">{shown.length} of {(rows || []).length}</span>
                    {csv && <button type="button" onClick={() => downloadCsv(csvName, toCsv(shown, columns))} className="px-3 py-2 text-sm bg-slate-100 border border-slate-300 rounded hover:bg-slate-200">CSV</button>}
                    {actions}
                </div>
            )}
            {note && <p className="text-xs text-slate-500 -mt-2 mb-2 print:hidden">{note}</p>}
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-slate-200 text-left">
                            {columns.map((c) => (
                                <th key={c.key} onClick={() => clickSort(c)} aria-sort={sort && sort.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                                    className={`py-2 pr-4 text-xs uppercase tracking-wide text-slate-500 whitespace-nowrap ${c.sortable === false ? '' : 'cursor-pointer select-none hover:text-slate-800'}`}>
                                    {c.label}{arrow(c)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {shown.length === 0 && <tr><td colSpan={columns.length} className="py-6 text-center text-slate-400">{(rows || []).length ? 'No rows match.' : empty}</td></tr>}
                        {shown.map((r, i) => (
                            <tr key={keyOf(r, i)} onClick={onRowClick ? () => onRowClick(r) : undefined}
                                className={`border-b border-slate-50 ${onRowClick ? 'cursor-pointer hover:bg-slate-50' : ''} ${rowClass ? rowClass(r) || '' : ''}`}>
                                {columns.map((c) => <td key={c.key} className="py-2 pr-4 align-top">{c.render ? c.render(r) : fmt(cellValue(r, c), c.type)}</td>)}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
